import { useCallback, useEffect, useRef, useState } from 'react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
    ApiCallError, getSignPreview, signDocumentApi, getDocumentUrl, fetchPreviewBlob,
} from '@/api/client';
import { parsePlacement } from '@/lib/utils';
import { openPdf, isCancelledRender, type PdfHandle } from '@/lib/pdfPage';
import type { DocumentDto, SignPreviewDto } from '@/api/types';

/**
 * DRAG THE LOAD-RECEIVED STAMP ONTO THE SCAN, THEN PRINT IT (#110).
 *
 * ⭐ THE FLOW THIS SERVES (Pete, 2026-08-19). The driver's paperwork is scanned
 * in. Today somebody carries it back across the yard for a biro signature and
 * feeds it through the scanner a second time. This stamps the scan we already
 * have and produces a printable copy, so the second pass stops happening.
 *
 * ⭐ WHY IT IS DRAGGABLE AT ALL. Pete: "the docs will be different for each
 * carrier." There is no fixed corner that is blank on every bill of lading, so
 * the position has to be a human decision — and having made it once for a
 * carrier, the server hands the same spot back next time.
 *
 * ⭐ THE REAL PAGE IS DRAWN BEHIND THE STAMP (F120, Decision 302). Pete,
 * 2026-09-29: *"Can we fix this to show the document behind it so we know where to
 * place the signature?"* → *"Exact page render"*. The follow-up the first version
 * promised. pdf.js draws the page the server stamps — the VISIBLE page, /Rotate
 * applied (lib/pdfPage.ts says why that is the same page) — and the sheet takes
 * that page's own shape, so a landscape scan is a landscape sheet.
 *
 * ⭐ AND THE STAMP IS DRAWN AT ITS PRINTED SIZE AND PLACE. The drop point is the
 * first line's baseline start, the panel hangs below it, and its font, line height
 * and padding are the server's (`documentSigning.ts`), scaled from points to this
 * sheet. ⛔ Those three ratios are pinned against the server by signing-guard.
 *
 * ⚠️ WHEN THE PAGE CANNOT BE DRAWN (not a PDF, a damaged file, an old browser) the
 * dialog falls back to the blank page-shaped sheet and says so — never a blank
 * rectangle that looks like a document.
 *
 * ⛔ AND NOTHING HERE COMPOSES THE STAMP. The lines come from the server, which
 * is where they are also drawn — the DoorTextDialog rule, for the same reason:
 * a UI that builds its own copy eventually shows one thing and sends another.
 */

interface Props {
    open: boolean;
    document: DocumentDto | null;
    facilityCode: string;
    /** Scoped lookup for "where did this carrier's stamp go last time". */
    carrierCode?: string | null;
    appointmentNumber?: string | null;
    getToken: () => Promise<string>;
    onClose: () => void;
    /** The signed copy landed — the paperwork list needs to show two documents. */
    onSigned?: (signed: DocumentDto) => void;
}

/** Bottom-right, inside the margin — where a receiving stamp usually goes. */
const DEFAULT_PLACE = { page: 1, fx: 0.62, fy: 0.88 };

/** US Letter in points — the fallback sheet's shape when the page cannot be drawn. */
const LETTER = { width: 612, height: 792 };

/**
 * ⛔ THE SERVER'S STAMP GEOMETRY, as ratios of the font size (`documentSigning.ts`:
 * `lineHeight = size * 1.25`, `padX = size * 0.5`, `padY = size * 0.35`). Pinned
 * against that file by signing-guard, so the two cannot drift apart silently.
 */
const STAMP_LINE_HEIGHT = 1.25;
const STAMP_PAD_X = 0.5;
const STAMP_PAD_Y = 0.35;

type PageState = 'loading' | 'ready' | 'notPdf' | 'failed';

export function SignDocumentDialog({
    open, document: doc, facilityCode, carrierCode, appointmentNumber,
    getToken, onClose, onSigned,
}: Props) {
    const [preview, setPreview] = useState<SignPreviewDto | null>(null);
    const [place, setPlace] = useState(DEFAULT_PLACE);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [signed, setSigned] = useState<DocumentDto | null>(null);
    const [nameAltered, setNameAltered] = useState(false);
    const sheet = useRef<HTMLDivElement | null>(null);
    const canvas = useRef<HTMLCanvasElement | null>(null);
    const [pdf, setPdf] = useState<PdfHandle | null>(null);
    const [pageState, setPageState] = useState<PageState>('loading');
    const [pageSize, setPageSize] = useState<{ width: number; height: number } | null>(null);
    const [sheetPx, setSheetPx] = useState(0);

    const load = useCallback(async () => {
        if (!doc) return;
        setLoading(true);
        setError(null);
        try {
            const res = await getSignPreview(await getToken(), doc.documentId, facilityCode, {
                carrier: carrierCode ?? null,
                appointment: appointmentNumber ?? null,
            });
            setPreview(res);
            setSigned(res.alreadySigned);
            // ⭐ START WHERE THIS CARRIER'S LAST ONE WENT. A site doing the same
            // carrier forty times a week should drag the box once, not forty times.
            setPlace(parsePlacement(res.lastPlacement) ?? DEFAULT_PLACE);
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : 'Could not read this document.');
        } finally {
            setLoading(false);
        }
    }, [doc, facilityCode, carrierCode, appointmentNumber, getToken]);

    useEffect(() => {
        if (!open) return;
        setPreview(null);
        setSigned(null);
        setNameAltered(false);
        setError(null);
        void load();
    }, [open, load]);

    /**
     * ⭐ FETCH AND OPEN THE SCAN once the stamp is on (not when it is switched off or
     * already signed). The bytes come through the same bounded preview route the
     * paperwork viewer uses; the object URL is revoked as soon as it is read.
     */
    const stampable = !!preview?.enabled && !preview?.alreadySigned;
    useEffect(() => {
        if (!open || !doc || !stampable) return;
        let alive = true;
        let handle: PdfHandle | null = null;
        setPageState('loading');
        setPageSize(null);
        void (async () => {
            let url: string | null = null;
            try {
                const got = await fetchPreviewBlob(await getToken(), { kind: 'document', documentId: doc.documentId });
                url = got.url;
                if (got.contentType !== 'application/pdf') {
                    if (alive) setPageState('notPdf');
                    return;
                }
                const bytes = await (await fetch(got.url)).arrayBuffer();
                handle = await openPdf(bytes);
                if (!alive) { handle.destroy(); handle = null; return; }
                setPdf(handle);
                setPageState('ready');
            } catch {
                if (alive) setPageState('failed');
            } finally {
                if (url) URL.revokeObjectURL(url);
            }
        })();
        return () => {
            alive = false;
            handle?.destroy();
            setPdf(null);
        };
    }, [open, doc, stampable, getToken]);

    /** The drawn page's own shape, in points. */
    const pageCount = pdf?.pageCount ?? null;
    const pageNo = pageCount ? Math.min(place.page, pageCount) : place.page;
    useEffect(() => {
        if (!pdf) return;
        let alive = true;
        pdf.pageSize(pageNo)
            .then(sz => { if (alive) setPageSize(sz); })
            .catch(() => { if (alive) setPageState('failed'); });
        return () => { alive = false; };
    }, [pdf, pageNo]);

    /** The sheet's width in CSS pixels — what the page is drawn at and the stamp scaled by. */
    const showSheet = !!preview?.enabled && !signed;
    useEffect(() => {
        const el = sheet.current;
        if (!open || !showSheet || !el) return;
        const measure = () => setSheetPx(Math.round(el.getBoundingClientRect().width));
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [open, showSheet, pageState]);

    const drawn = pageState === 'ready' && !!pdf && !!pageSize;
    useEffect(() => {
        const el = canvas.current;
        if (!drawn || !pdf || !el || sheetPx <= 0) return;
        pdf.render(pageNo, el, sheetPx).catch(err => {
            if (!isCancelledRender(err)) setPageState('failed');
        });
        return () => pdf.cancel();
    }, [drawn, pdf, pageNo, sheetPx]);

    /** A click or drag on the sheet moves the stamp there. */
    const moveTo = (clientX: number, clientY: number) => {
        const el = sheet.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        if (r.width === 0 || r.height === 0) return;
        setPlace(p => ({
            ...p,
            // ⚠️ FRACTIONS FROM THE TOP-LEFT, which is how a div behaves and NOT
            // how a PDF does. The conversion to PDF's bottom-left space happens
            // once, on the server, in documentSignature.ts — doing it here as
            // well would be two implementations of one transform.
            fx: Math.min(1, Math.max(0, (clientX - r.left) / r.width)),
            fy: Math.min(1, Math.max(0, (clientY - r.top) / r.height)),
        }));
    };

    const dragging = useRef(false);

    async function sign() {
        if (!doc) return;
        setBusy(true);
        setError(null);
        try {
            const res = await signDocumentApi(await getToken(), doc.documentId, place);
            setSigned(res.signed);
            setNameAltered(res.nameWasAltered === true);
            onSigned?.(res.signed);
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : 'Could not stamp this document.');
        } finally {
            setBusy(false);
        }
    }

    /**
     * ⭐ OPEN THE SIGNED PDF AND LET THE BROWSER PRINT IT (Pete: "Opening the pdf
     * and printing is fine"). No print server, no driver, no mapped device —
     * the scanner/printer is already on the scheduler's machine and their own
     * print dialog knows about it. Anything cleverer would be a second thing to
     * configure per site for no gain.
     *
     * ⚠️ THE TAB IS OPENED BEFORE THE await, not after. Browsers block a window
     * opened from an async continuation because it is no longer inside the click
     * that the user made — the popup blocker fires and nothing happens, silently.
     */
    async function openSigned() {
        if (!signed) return;
        const tab = window.open('', '_blank', 'noopener,noreferrer');
        try {
            const { url } = await getDocumentUrl(await getToken(), signed.documentId);
            if (tab) tab.location.href = url;
            else window.location.href = url;
        } catch (err) {
            tab?.close();
            setError(err instanceof ApiCallError ? err.details.error : 'Could not open the signed copy.');
        }
    }

    const disabled = !preview?.enabled || busy || loading;

    /** The sheet's page (points): the drawn page, else Letter. */
    const sheetPage = drawn && pageSize ? pageSize : LETTER;
    /** Points → this sheet's pixels, for the stamp. */
    const ptPx = sheetPx > 0 ? sheetPx / sheetPage.width : 0.6;
    const fontPt = preview?.fontSize || 10;
    const stampPx = {
        size: fontPt * ptPx,
        lineHeight: fontPt * STAMP_LINE_HEIGHT * ptPx,
        padX: fontPt * STAMP_PAD_X * ptPx,
        padY: fontPt * STAMP_PAD_Y * ptPx,
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>Stamp this paperwork as received</DialogTitle>
                    <DialogDescription>
                        {doc?.fileName ?? 'This document'}
                        {/* ⛔ THE ASSERTION BEING MADE, said out loud before it is made.
                            Same discipline as the paperwork-ready text: a person is
                            about to state something on a carrier's document. */}
                        <span className="ml-1 text-muted-foreground">
                            · the original is kept untouched; this creates a second, signed copy
                        </span>
                    </DialogDescription>
                </DialogHeader>

                {loading && !preview && <div className="text-xs text-muted-foreground">Loading…</div>}

                {preview && !preview.enabled && (
                    <div className="rounded border border-amber-400 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
                        The appointment-received stamp is switched off for this facility. An admin can turn
                        it on under Settings → Documents.
                    </div>
                )}

                {preview?.enabled && (
                    <div className="space-y-3">
                        {signed && (
                            <div className="rounded border border-emerald-400 bg-emerald-50 px-2 py-1.5 text-xs text-emerald-900">
                                <div className="font-medium">{signed.fileName}</div>
                                <div>
                                    Stamped{signed.signedByName ? ` by ${signed.signedByName}` : ''}. The
                                    original is unchanged and both are on the appointment.
                                </div>
                                {nameAltered && (
                                    // ⚠️ SURFACED, NOT SWALLOWED. The standard PDF fonts
                                    // cannot encode every alphabet, and the person whose
                                    // name was altered should hear it from us rather than
                                    // from the carrier.
                                    <div className="mt-1 text-amber-800">
                                        A character in the name could not be printed and was replaced
                                        with “?”. Set a site name under Settings → Documents if that
                                        is not acceptable on a carrier&apos;s copy.
                                    </div>
                                )}
                            </div>
                        )}

                        {!signed && (
                            <>
                                <div className="flex items-center justify-between">
                                    <Label>Drag the stamp where you want it</Label>
                                    <div className="flex items-center gap-2 text-xs">
                                        <span className="text-muted-foreground">Page</span>
                                        <input
                                            type="number"
                                            min={1}
                                            max={pageCount ?? undefined}
                                            value={pageNo}
                                            onChange={(e) => setPlace(p => ({
                                                ...p,
                                                page: Math.min(pageCount ?? 9999, Math.max(1, Number(e.target.value) || 1)),
                                            }))}
                                            className="h-7 w-16 rounded border bg-background px-2"
                                            disabled={busy}
                                        />
                                        {pageCount !== null && <span className="text-muted-foreground">of {pageCount}</span>}
                                    </div>
                                </div>

                                {/* The page. ⭐ The scan itself when it could be drawn;
                                    otherwise a blank sheet that says it is one. */}
                                <div
                                    ref={sheet}
                                    role="application"
                                    aria-label="Stamp position"
                                    onPointerDown={(e) => {
                                        dragging.current = true;
                                        e.currentTarget.setPointerCapture(e.pointerId);
                                        moveTo(e.clientX, e.clientY);
                                    }}
                                    onPointerMove={(e) => {
                                        if (dragging.current) moveTo(e.clientX, e.clientY);
                                    }}
                                    onPointerUp={(e) => {
                                        dragging.current = false;
                                        e.currentTarget.releasePointerCapture(e.pointerId);
                                    }}
                                    className="relative mx-auto cursor-crosshair select-none overflow-hidden
                                               rounded border bg-white shadow-inner"
                                    style={{
                                        aspectRatio: `${sheetPage.width} / ${sheetPage.height}`,
                                        // ⭐ As wide as fits, and never taller than the screen allows.
                                        width: `min(100%, calc(62vh * ${sheetPage.width / sheetPage.height}))`,
                                    }}
                                >
                                    {drawn && (
                                        <canvas
                                            ref={canvas}
                                            aria-label={`Page ${pageNo} of the scan`}
                                            className="pointer-events-none absolute inset-0 h-full w-full"
                                        />
                                    )}
                                    {!drawn && (
                                        <>
                                            <div className="pointer-events-none absolute inset-3 rounded border border-dashed border-slate-200" />
                                            <div className="pointer-events-none absolute inset-x-0 top-6 text-center text-[10px] uppercase tracking-wide text-slate-300">
                                                {pageState === 'loading'
                                                    ? 'drawing the page…'
                                                    : `page ${pageNo} — position only`}
                                            </div>
                                        </>
                                    )}

                                    {/* The stamp, at the size and place it will print:
                                        the drop point is the first line's baseline start. */}
                                    <div
                                        className="pointer-events-none absolute whitespace-pre rounded border
                                                   border-slate-500 bg-white/75 text-slate-900 shadow-sm"
                                        style={{
                                            left: `calc(${place.fx * 100}% - ${stampPx.padX}px)`,
                                            top: `calc(${place.fy * 100}% - ${stampPx.size + stampPx.padY}px)`,
                                            padding: `${stampPx.padY}px ${stampPx.padX}px`,
                                            fontSize: `${stampPx.size}px`,
                                            lineHeight: `${stampPx.lineHeight}px`,
                                            fontFamily: 'Helvetica, Arial, sans-serif',
                                        }}
                                    >
                                        {preview.lines.map((l, i) => (
                                            <div key={i} className={i === 0 ? 'font-semibold' : ''}>{l}</div>
                                        ))}
                                    </div>
                                </div>

                                <div className="text-[11px] text-muted-foreground">
                                    {/* ⚠️ HONEST ABOUT WHAT IS ON SCREEN. */}
                                    {drawn ? (
                                        <>This is page {pageNo}{pageCount ? ` of ${pageCount}` : ''} of the scan,
                                        with the stamp at the size and place it will print — drop it on a
                                        clear part of the page.</>
                                    ) : pageState === 'loading' ? (
                                        <>Drawing the page…</>
                                    ) : (
                                        <>{pageState === 'notPdf'
                                            ? 'This file is not a PDF, so the page cannot be drawn. '
                                            : 'The page could not be drawn. '}
                                        This is a blank page of the same proportions, not the scan itself —
                                        drop the stamp on a clear part of the layout and check the signed
                                        copy afterwards.</>
                                    )}{' '}
                                    {preview.previewTimesAreIllustrative && (
                                        <>The date and time shown are placeholders; the real ones are
                                        stamped from the server clock the moment you press the button.</>
                                    )}{' '}
                                    If the stamp would run off the edge it is nudged back inside the
                                    margin.
                                </div>
                            </>
                        )}
                    </div>
                )}

                {error && (
                    <div className="rounded border border-destructive/40 bg-destructive/5 px-2 py-1 text-xs text-destructive">
                        {error}
                    </div>
                )}

                <DialogFooter>
                    <div className="flex w-full justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
                            {signed ? 'Close' : 'Cancel'}
                        </Button>
                        {signed ? (
                            <Button size="sm" onClick={() => void openSigned()}>
                                Open and print
                            </Button>
                        ) : (
                            <Button size="sm" onClick={() => void sign()} disabled={disabled}>
                                {busy ? 'Stamping…' : 'Stamp and save a signed copy'}
                            </Button>
                        )}
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
